/**
 * Error Interceptor
 *
 * More on Interceptors {@link https://docs.angularjs.org/api/ng/service/$http#interceptors}
 *
 * All of the methods are optional.
 *
 * Before you alter the interceptors make sure you understand the [$q and deferred/promise APIs](https://docs.angularjs.org/api/ng/service/$q)
 *
 * For purposes of global error handling, authentication, or any kind of synchronous or asynchronous
 * pre-processing of request or postprocessing of responses, it is desirable to be able to intercept requests
 * before they are handed to the server and responses before they are handed over to the application code
 * that initiated these requests. The interceptors leverage the promise APIs to fulfill this need for both
 * synchronous and asynchronous pre-processing.
 */

/* @ngInject */
export default function ErrorInterceptor($q, $log, $translate, NotifyingService) {
    'use strict';

    // ==== Error Handling
    function handleStatus(status, text, data) {
        let supportingText = '';

        if (text) {
            supportingText = text;
        } else {
            supportingText = status;
        }

        if (status !== undefined) {
            switch (status) {
                case 200 :
                    // weird case, that may happen
                    $log.debug(supportingText + ': Everything worked okay, but Angular thought it was a Promise rejection.');
                    return status;
                // 4xx Client Errors
                case 401 :
                    $log.error(supportingText + ': This request is not authorized by the server.');
                    break;
                case 404 :
                    $log.debug(supportingText + ': This resource was not found by the server.');
                    break;
                case 429 :
                    throw new Error(supportingText + ': Rate limit exceeded try again in 5 minutes.');
                // 5xx Server Errors
                case 500 :
                    if (data !== undefined && JSON.stringify(data).toLowerCase().indexOf('too many requests') !== -1) {
                        throw new Error('Too Many Requests: Rate limit exceeded try again in 5 minutes.');
                    } else {
                        $log.error(supportingText + ': Something went wrong when the server was trying to handle the request.');
                        break;
                    }
                default:
                    // general catching, for errors we haven't anticipated.
                    $log.debug(supportingText + ': Weird error happened.');
            }
        }
    }

    function handleError(error) {
        try {
            handleStatus(error.status, error.statusText, error.data);
        } catch (e) {
            if (error.status === 429 || (error.status === 500 && error.data !== undefined && JSON.stringify(error.data).toLowerCase().indexOf('too many requests') !== -1)) {
                NotifyingService.messageNotification({
                    alertLevel: 'error',
                    message: $translate.instant('message__server__rate_limit').valueOf()
                });
            } else {
                NotifyingService.messageNotification({
                    alertLevel: 'error',
                    message: $translate.instant('message__server__generic').valueOf()
                });
            }

            $log.error(error, e);
        }
    }

    return {
        /**
         * Interceptor gets called when a previous interceptor threw an error or resolved with a rejection.
         * @param rejection
         * @returns {void | *}
         */
        requestError(rejection) {
            handleError(rejection);
            return $q.reject(rejection);
        },
        /**
         * Interceptor gets called when a previous interceptor threw an error or resolved with a rejection.
         * @param rejection
         * @returns {void | *}
         */
        responseError(rejection) {
            handleError(rejection);
            return $q.reject(rejection);
        },
        request(config) {
            $log.debug('config url', config.url);
            return config;
        }
    };
}
